import { renderToStaticMarkup } from 'react-dom/server';
import { createElement, Fragment } from 'react';
import type { ReportSection as ReportSectionType, ReportTheme } from '@/types/report';
import { ReportSection } from './ReportSection';
import { getTheme } from './themes';
import reportCssRaw from './report.css?raw';

/**
 * Build a fully-themed standalone HTML document for a report — same shape
 * the download flow produces (see ReportModule.handleDownload), but driven
 * by section data alone so it can run anywhere (e.g. inside the Pages list
 * row thumbnail useMemo, where ReportModule isn't mounted).
 *
 * Renders the same ReportSection components the live preview uses, wraps
 * them with the theme CSS vars + report.css. Result: thumbnail iframes for
 * reports look identical to a real report screen, instead of the plain-text
 * fallback that previously read as "random lines of text."
 */
export function synthesizeReportHtml(
  label: string,
  sections: ReportSectionType[] | undefined,
  themeName: ReportTheme | undefined,
): string {
  const sortedSections: ReportSectionType[] = Array.isArray(sections)
    ? [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const theme = getTheme(themeName ?? 'formal');
  const cssVars = Object.entries(theme.variables)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}: ${v};`)
    .join('\n      ');

  const fontsLink = theme.fontsUrl
    ? `<link rel="stylesheet" href="${theme.fontsUrl}" />`
    : '';

  const sectionsHtml = renderToStaticMarkup(
    createElement(
      Fragment,
      null,
      sortedSections.map((s) => createElement(ReportSection, { key: s.id, section: s })),
    ),
  );

  const escapedTitle = (label || 'Report').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapedTitle}</title>
  ${fontsLink}
  <style>
    :root {
      ${cssVars}
    }
    body { margin: 0; background: var(--report-bg, #fff); color: var(--report-text, #1a1a1a); }
    ${reportCssRaw}
    /* Skip section entry animations — thumbnails are static. */
    .report-section { opacity: 1 !important; transform: none !important; animation: none !important; }
  </style>
</head>
<body>
  <div class="report-container">
    ${sectionsHtml}
  </div>
</body>
</html>`;
}
